import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, ChevronRight, CheckCircle, Gift, RotateCcw, X, AlertTriangle, Monitor, Mail, Package, Bell, Ban, Clock, Wifi } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, FilterSelect, PageHeader } from '../components/FilterBar';
import SearchableSelect from '../components/SearchableSelect';

const REMINDER_DAYS = 3; // matches the scheduled job in backend/services/scheduledJobs.js
const CLOSED = ['completed', 'cancelled']; // no reminders or checklist actions once closed

const fmtDT = (d) => d
  ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

// Date the automatic reminder goes out (last working day minus REMINDER_DAYS)
function autoReminderDate(dateStr) {
  const d = new Date(dateStr); d.setDate(d.getDate() - REMINDER_DAYS);
  return d.toLocaleDateString('en-GB');
}

const EMAIL_STATUS_TEXT = {
  sent: 'sent',
  smtp_not_configured: 'not sent — email (SMTP) is not configured on this server',
  disabled: 'not sent — switched off in Settings → Emails',
  failed: 'failed — check the server log',
};

// Whole days from today to the given date (negative = already past)
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr); target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
}

export default function Offboarding() {
  const { isIT, isAdmin } = useAuth();
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false); const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [actionModal, setActionModal] = useState(null); // { type:'asset'|'system'|'email', item, options }
  const [actionForm, setActionForm] = useState({});
  const [remindingId, setRemindingId] = useState(null);
  const [remindMsg, setRemindMsg] = useState(null); // { id, text, ok }
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelResult, setCancelResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
      const r = await api.get('/offboarding', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, filterStatus, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/employees', { params: { limit: 1000, status: 'active' } }).then(r => setEmployees(r.data.data || [])).catch(() => {}); }, []);

  const loadChecklist = async (id) => {
    const r = await api.get(`/offboarding/${id}`);
    setChecklist(r.data);
  };

  // Hand a dongle or voice SIM back during offboarding. 'return' keeps the line
  // active so it can be reassigned; 'disconnect' terminates it with the provider.
  const handleConnection = async (cn, action) => {
    const what = cn.type === 'dongle' ? 'dongle' : 'voice number';
    if (action === 'disconnect' && !window.confirm(`Disconnect ${what} ${cn.number}?\n\nThe line will be terminated with the provider. Use "Mark Returned" instead if it is to be reassigned to someone else.`)) return;
    try {
      await api.post(`/offboarding/${selected.id}/connection/${cn.type}/${cn.id}`, { action });
      await loadChecklist(selected.id);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); }
  };

  const openChecklist = async (ob) => {
    setSelected(ob);
    await loadChecklist(ob.id);
    setModal('checklist');
  };

  const handleInitiate = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/offboarding', form);
      setModal(null); setForm({}); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const handleAction = async () => {
    setSaving(true); setError('');
    try {
      const { type, item } = actionModal;
      if (type === 'asset') {
        await api.post(`/offboarding/${selected.id}/asset/${item.id}`, actionForm);
      } else if (type === 'system') {
        await api.post(`/offboarding/${selected.id}/system/${item.id}`, actionForm);
      } else if (type === 'email') {
        await api.post(`/offboarding/${selected.id}/email/${item.id}`, actionForm);
      }
      setActionModal(null); setActionForm({});
      await loadChecklist(selected.id);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const handleComplete = async () => {
    if (!window.confirm('Mark this offboarding as COMPLETE? This cannot be undone.')) return;
    setSaving(true); setError('');
    try {
      await api.post(`/offboarding/${selected.id}/complete`);
      setModal(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Cannot complete — some items still pending'); } finally { setSaving(false); }
  };

  const StatusIcon = ({ done }) => done
    ? <CheckCircle size={14} style={{color:'#34d399'}}/>
    : <AlertTriangle size={14} style={{color:'#f59e0b'}}/>;

  const isActive = !!checklist && !CLOSED.includes(checklist.status);
  const reminderLog = Array.isArray(checklist?.reminders) ? checklist.reminders : [];

  // Everything the leaver still holds — accessories and dongle/voice lines were
  // not counted before, so the panel could say "ready to complete" while a SIM
  // was still charged to them (the API now refuses that too).
  const cl = checklist?.checklist;
  const openAssets      = cl?.assets?.length || 0;
  const openAccessories = cl?.accessories?.length || 0;
  const openLines       = cl?.connections?.length || 0;
  const openSystems     = cl?.systems?.filter(s => s.status === 'active').length || 0;
  const openEmails      = cl?.emails?.filter(e => e.status === 'active').length || 0;
  const allDone = !!checklist && openAssets === 0 && openAccessories === 0 && openLines === 0
    && openSystems === 0 && openEmails === 0;
  const stillOpen = [
    openAssets      && `${openAssets} asset${openAssets > 1 ? 's' : ''}`,
    openAccessories && `${openAccessories} accessor${openAccessories > 1 ? 'ies' : 'y'}`,
    openLines       && `${openLines} dongle/voice line${openLines > 1 ? 's' : ''}`,
    openSystems     && `${openSystems} system login${openSystems > 1 ? 's' : ''}`,
    openEmails      && `${openEmails} email account${openEmails > 1 ? 's' : ''}`,
  ].filter(Boolean);

  const sendReminder = async (r) => {
    setRemindingId(r.id); setRemindMsg(null);
    try {
      const res = await api.post(`/offboarding/${r.id}/send-reminder`);
      setRemindMsg({ id: r.id, text: `✓ Reminder #${res.data?.reminder_count || ''} sent`, ok: true });
      load();
      if (modal === 'checklist' && selected?.id === r.id) await loadChecklist(r.id);
    } catch (e) {
      setRemindMsg({ id: r.id, text: e.response?.data?.error || 'Failed', ok: false });
    } finally {
      setRemindingId(null);
      setTimeout(() => setRemindMsg(null), 5000);
    }
  };

  const openCancel = () => { setCancelReason(''); setCancelError(''); setCancelResult(null); setCancelOpen(true); };

  const handleCancel = async () => {
    if (cancelReason.trim().length < 5) { setCancelError('Please enter a reason (at least 5 characters).'); return; }
    setCancelling(true); setCancelError('');
    try {
      const res = await api.post(`/offboarding/${selected.id}/cancel`, { reason: cancelReason.trim() });
      setCancelResult(res.data);
      await loadChecklist(selected.id);
      load();
    } catch (err) { setCancelError(err.response?.data?.error || 'Failed to cancel the offboarding'); }
    finally { setCancelling(false); }
  };

  const columns = [
    { header: 'Employee', render: r => <div><div className="font-medium text-sm" style={{color:'#e2e8f0'}}>{r.employee?.full_name || '—'}</div><div className="text-xs" style={{color:'#334155'}}>{r.employee?.designation}</div></div> },
    { header: 'Resignation Date', key: 'resignation_date' },
    { header: 'Last Day', render: r => {
      if (!r.last_working_date) return <span style={{color:'#334155'}}>—</span>;
      const d = daysUntil(r.last_working_date);
      const due = !CLOSED.includes(r.status) && d !== null && d >= 0 && d <= REMINDER_DAYS;
      return (
        <div>
          <div style={{color:'#e2e8f0'}}>{r.last_working_date}</div>
          {!CLOSED.includes(r.status) && d !== null && (
            <div className="text-xs" style={{color: d < 0 ? '#f87171' : due ? '#fbbf24' : '#475569'}}>
              {d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'Today' : `in ${d}d`}
            </div>
          )}
        </div>
      );
    }},
    { header: 'Handover Reminder', render: r => {
      const log = Array.isArray(r.reminders) ? r.reminders : [];
      const count = r.reminder_count || log.length;
      const last = log[log.length - 1];
      if (CLOSED.includes(r.status)) {
        return <span className="text-xs" style={{color:'#334155'}}>{count ? `${count} sent` : '—'}</span>;
      }
      const msg = remindMsg?.id === r.id ? remindMsg : null;
      return (
        <div className="flex items-center gap-2">
          {count ? (
            <span className="text-xs" style={{color:'#34d399'}} title={last?.at ? `Last: ${fmtDT(last.at)}${last.by_name ? ` by ${last.by_name}` : ''}` : ''}>
              ✓ {count} sent{last?.at ? ` · last ${new Date(last.at).toLocaleDateString('en-GB')}` : ''}
            </span>
          ) : (
            <span className="text-xs" style={{color:'#475569'}}>Not sent</span>
          )}
          {isAdmin && (
            <button onClick={() => sendReminder(r)} disabled={remindingId === r.id}
              className="btn-ghost px-1.5 py-1" title="Send handover reminder now (same email, same recipients)"
              style={{color:'#fbbf24'}}>
              <Bell size={11}/>
            </button>
          )}
          {msg && <span className="text-xs" style={{color: msg.ok ? '#34d399' : '#f87171'}}>{msg.text}</span>}
        </div>
      );
    }},
    { header: 'Status', render: r => (
      <div>
        <Badge value={r.status}/>
        {r.status === 'cancelled' && r.cancelled_at && (
          <div className="text-xs mt-0.5" style={{color:'#475569'}} title={r.cancellation_reason ? `Reason: ${r.cancellation_reason}` : ''}>
            {new Date(r.cancelled_at).toLocaleDateString('en-GB')}{r.cancelled_by_name ? ` · ${r.cancelled_by_name}` : ''}
          </div>
        )}
      </div>
    )},
    { header: 'Checklist', render: r => (
      <div className="flex items-center gap-2 text-xs">
        <span style={{color:r.assets_returned?'#34d399':'#f59e0b'}}>{r.assets_returned ? '✓' : '○'} Assets</span>
        <span style={{color:r.systems_disabled?'#34d399':'#f59e0b'}}>{r.systems_disabled ? '✓' : '○'} Systems</span>
        <span style={{color:r.emails_handled?'#34d399':'#f59e0b'}}>{r.emails_handled ? '✓' : '○'} Emails</span>
      </div>
    )},
    { header: '', render: r => (
      <button onClick={() => openChecklist(r)} className="btn-primary text-xs px-3 py-1.5">
        Open Checklist <ChevronRight size={11}/>
      </button>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Offboarding" count={total}>
        <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
        {isAdmin && <button onClick={() => { setForm({resignation_date:new Date().toISOString().split('T')[0]}); setError(''); setModal('initiate'); }} className="btn-primary"><Plus size={13}/> Initiate Offboarding</button>}
      </PageHeader>
      <FilterBar hasFilters={!!(filterStatus||dateFrom||dateTo)} onClear={() => { setFilterStatus(''); setDateFrom(''); setDateTo(''); }}>
        <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses" options={['initiated','in_progress','completed','cancelled'].map(s=>({value:s,label:s.replace('_',' ')}))} />
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
      </FilterBar>
      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage} />

      {/* INITIATE MODAL */}
      {modal === 'initiate' && (
        <Modal title="Initiate Offboarding" onClose={() => setModal(null)}>
          <form onSubmit={handleInitiate} className="space-y-4">
            <ErrMsg msg={error} />
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Employee *</label>
              <SearchableSelect required value={form.employee_id || ''}
                onChange={v => setForm({...form, employee_id: v})}
                options={employees.map(e => ({ value: e.id, label: e.full_name,
                  hint: [e.employee_code, e.designation].filter(Boolean).join(' · ') || undefined }))}
                placeholder="Select employee"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FF label="Resignation Date" name="resignation_date" type="date" form={form} setForm={setForm} required />
              <FF label="Last Working Date" name="last_working_date" type="date" form={form} setForm={setForm} />
            </div>
            <FF label="Remarks" name="remarks" form={form} setForm={setForm} rows={3} placeholder="Any notes about this offboarding..." />
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Initiating...':'Initiate Offboarding'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* CHECKLIST MODAL */}
      {modal === 'checklist' && checklist && (
        <Modal title={`Offboarding: ${selected?.employee?.full_name}`} onClose={() => { setModal(null); setChecklist(null); }} size="xl">
          <div className="space-y-5">
            {error && <ErrMsg msg={error} />}

            {checklist.status === 'cancelled' && (
              <div className="rounded-xl p-4" style={{background:'rgba(100,116,139,0.10)',border:'1px solid rgba(148,163,184,0.3)'}}>
                <div className="flex items-center gap-2">
                  <Ban size={16} style={{color:'#94a3b8'}}/>
                  <span className="font-semibold text-sm" style={{color:'#cbd5e1'}}>Offboarding cancelled — employee continues</span>
                </div>
                <div className="text-xs mt-1.5" style={{color:'#94a3b8'}}>
                  {fmtDT(checklist.cancelled_at)}{checklist.cancelled_by_name ? ` · by ${checklist.cancelled_by_name}` : ''}
                </div>
                {checklist.cancellation_reason && (
                  <div className="text-xs mt-1" style={{color:'#cbd5e1'}}>Reason: {checklist.cancellation_reason}</div>
                )}
                <div className="text-xs mt-1" style={{color:'#64748b'}}>This record is read-only. Initiate a new offboarding if the employee leaves after all.</div>
              </div>
            )}

            {/* Employee Info */}
            <div className="glass rounded-xl p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
                style={{background:'linear-gradient(135deg,rgba(59,130,246,0.2),rgba(99,102,241,0.2))',color:'#60a5fa',border:'1px solid rgba(59,130,246,0.2)'}}>
                {selected?.employee?.full_name?.charAt(0)}
              </div>
              <div className="flex-1">
                <div className="font-semibold" style={{color:'#e2e8f0'}}>{selected?.employee?.full_name}</div>
                <div className="text-xs" style={{color:'#475569'}}>{selected?.employee?.designation} · Resignation: {selected?.resignation_date} · Last Day: {selected?.last_working_date || 'Not set'}</div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <StatusIcon done={checklist.assets_returned} /><span style={{color:'#475569'}}>Assets</span>
                <StatusIcon done={checklist.systems_disabled} /><span style={{color:'#475569'}}>Systems</span>
                <StatusIcon done={checklist.emails_handled} /><span style={{color:'#475569'}}>Emails</span>
              </div>
            </div>

            {/* HANDOVER REMINDERS — same email and recipients as the automatic 3-day reminder */}
            <div className="rounded-xl p-4" style={{background:'rgba(217,119,6,0.06)',border:'1px solid rgba(217,119,6,0.2)'}}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Bell size={14} style={{color:'#fbbf24'}}/>
                  <span className="text-sm font-semibold" style={{color:'#fbbf24'}}>Handover Reminders ({reminderLog.length})</span>
                </div>
                {isAdmin && isActive && (
                  <button onClick={() => sendReminder(checklist)} disabled={remindingId === checklist.id}
                    className="text-xs px-3 py-1.5 rounded-lg font-medium"
                    style={{background:'rgba(217,119,6,0.15)',color:'#fbbf24',border:'1px solid rgba(217,119,6,0.35)',cursor:remindingId === checklist.id ? 'default' : 'pointer'}}>
                    {remindingId === checklist.id ? 'Sending…' : '🔔 Send Reminder Now'}
                  </button>
                )}
              </div>
              {checklist.recipients && (
                <div className="text-xs mt-2" style={{color:'#94a3b8'}}>
                  <span style={{color:'#64748b'}}>To:</span> {checklist.recipients.to || <span style={{color:'#f87171'}}>no email on file</span>}
                  <span style={{color:'#64748b'}}> · CC:</span> {checklist.recipients.cc?.length ? checklist.recipients.cc.join(', ') : '—'}
                  {!checklist.recipients.hod_email && <span style={{color:'#64748b'}}> (no HOD email on file)</span>}
                </div>
              )}
              {isActive && checklist.last_working_date && (
                <div className="text-xs mt-1" style={{color:'#64748b'}}>
                  Automatic reminder: {checklist.handover_reminder_sent
                    ? `sent ${fmtDT(checklist.handover_reminder_at)}`
                    : `due ${REMINDER_DAYS} days before the last day (${autoReminderDate(checklist.last_working_date)})`}
                </div>
              )}
              {remindMsg?.id === checklist.id && (
                <div className="text-xs mt-1" style={{color: remindMsg.ok ? '#34d399' : '#f87171'}}>{remindMsg.text}</div>
              )}
              {reminderLog.length > 0 ? (
                <div className="mt-2 space-y-1">
                  {reminderLog.map((rm, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs flex-wrap" style={{color:'#94a3b8'}}>
                      <span style={{color:'#fbbf24',fontVariantNumeric:'tabular-nums'}}>#{i + 1}</span>
                      <Clock size={11} style={{color:'#64748b'}}/>
                      <span style={{color:'#cbd5e1'}}>{fmtDT(rm.at)}</span>
                      <span className="px-1.5 py-0.5 rounded" style={{fontSize:10,background:rm.type === 'auto' ? 'rgba(59,130,246,0.15)' : 'rgba(217,119,6,0.15)',color:rm.type === 'auto' ? '#60a5fa' : '#fbbf24'}}>
                        {rm.type === 'auto' ? 'Automatic' : 'Manual'}
                      </span>
                      {rm.by_name && <span style={{color:'#64748b'}}>by {rm.by_name}</span>}
                      {rm.items != null && <span style={{color:'#475569'}}>· {rm.items} item{rm.items === 1 ? '' : 's'} outstanding</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs mt-2" style={{color:'#475569'}}>No reminders sent yet.</div>
              )}
            </div>

            {/* ASSETS */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Package size={14} style={{color:'#60a5fa'}}/>
                <span className="text-sm font-semibold" style={{color:'#94a3b8'}}>Assets Held ({checklist.checklist?.assets?.length || 0})</span>
                {checklist.checklist?.assets?.length === 0 && <span className="text-xs" style={{color:'#34d399'}}>✓ All cleared</span>}
              </div>
              {checklist.checklist?.assets?.length > 0 ? (
                <div className="space-y-2">
                  {checklist.checklist.assets.map(asset => (
                    <div key={asset.id} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                      <div className="flex-1">
                        <div className="text-sm font-mono" style={{color:'#60a5fa'}}>{asset.asset_number}</div>
                        <div className="text-xs" style={{color:'#475569'}}>{asset.brand} {asset.model} · <Badge value={asset.category}/></div>
                      </div>
                      <Badge value={asset.status}/>
                      {isIT && isActive && (
                        <div className="flex gap-2">
                          <button onClick={() => { setActionModal({type:'asset',item:asset}); setActionForm({action:'return',remarks:''}); setError(''); }}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg" style={{background:'rgba(16,185,129,0.15)',color:'#34d399',border:'1px solid rgba(16,185,129,0.25)'}}>
                            <RotateCcw size={11}/> Return
                          </button>
                          <button onClick={() => { setActionModal({type:'asset',item:asset}); setActionForm({action:'donate',remarks:''}); setError(''); }}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg" style={{background:'rgba(168,85,247,0.15)',color:'#c084fc',border:'1px solid rgba(168,85,247,0.25)'}}>
                            <Gift size={11}/> Donate
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-3 rounded-xl text-sm" style={{background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.15)',color:'#34d399'}}>
                  No assets pending — all returned or donated
                </div>
              )}
            </div>

            {/* SYSTEMS */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Monitor size={14} style={{color:'#818cf8'}}/>
                <span className="text-sm font-semibold" style={{color:'#94a3b8'}}>System Access ({checklist.checklist?.systems?.filter(s=>s.status==='active').length || 0} active)</span>
              </div>
              {checklist.checklist?.systems?.length > 0 ? (
                <div className="space-y-2">
                  {checklist.checklist.systems.map(sys => (
                    <div key={sys.id} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={{background: sys.status!=='active'?'rgba(16,185,129,0.06)':'rgba(255,255,255,0.03)',
                              border:`1px solid ${sys.status!=='active'?'rgba(16,185,129,0.2)':'rgba(255,255,255,0.06)'}`}}>
                      <div className="flex-1">
                        <div className="text-sm font-medium" style={{color:'#818cf8'}}>{sys.system_name}</div>
                        <div className="text-xs" style={{color:'#475569'}}>{sys.username}{sys.access_level ? ` · ${sys.access_level}` : ''}</div>
                        {sys.disabled_date && <div className="text-xs mt-0.5" style={{color:'#334155'}}>Deactivated: {sys.disabled_date}</div>}
                      </div>
                      {sys.status !== 'active' ? (
                        <span className="flex items-center gap-1 text-xs font-semibold" style={{color:'#34d399'}}>
                          ✓ {sys.status.replace('_',' ')}
                        </span>
                      ) : <Badge value={sys.status}/>}
                      {isIT && isActive && sys.status === 'active' && (
                        <div className="flex gap-2">
                          <button onClick={() => { setActionModal({type:'system',item:sys}); setActionForm({action:'disabled',remarks:''}); setError(''); }}
                            className="text-xs px-3 py-1.5 rounded-lg" style={{background:'rgba(239,68,68,0.15)',color:'#f87171',border:'1px solid rgba(239,68,68,0.25)'}}>
                            Disable
                          </button>
                          <button onClick={() => { setActionModal({type:'system',item:sys}); setActionForm({action:'password_changed',remarks:''}); setError(''); }}
                            className="text-xs px-3 py-1.5 rounded-lg" style={{background:'rgba(245,158,11,0.15)',color:'#fbbf24',border:'1px solid rgba(245,158,11,0.25)'}}>
                            Pwd Changed
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs px-4 py-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)',color:'#334155'}}>No system access records found</div>}
            </div>

            {/* EMAILS */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Mail size={14} style={{color:'#34d399'}}/>
                <span className="text-sm font-semibold" style={{color:'#94a3b8'}}>Email IDs ({checklist.checklist?.emails?.filter(e=>e.status==='active').length || 0} active)</span>
              </div>
              {checklist.checklist?.emails?.length > 0 ? (
                <div className="space-y-2">
                  {checklist.checklist.emails.map(email => (
                    <div key={email.id} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={{background: email.status!=='active'?'rgba(16,185,129,0.06)':'rgba(255,255,255,0.03)',
                              border:`1px solid ${email.status!=='active'?'rgba(16,185,129,0.2)':'rgba(255,255,255,0.06)'}`}}>
                      <div className="flex-1">
                        <div className="text-sm font-mono" style={{color:'#34d399'}}>{email.email_address}</div>
                        <div className="text-xs" style={{color:'#475569'}}>{email.email_type}</div>
                        {email.disabled_date && <div className="text-xs mt-0.5" style={{color:'#334155'}}>Deactivated: {email.disabled_date}</div>}
                        {email.forwarded_to && <div className="text-xs mt-0.5" style={{color:'#818cf8'}}>Forwarded → {email.forwarded_to}</div>}
                      </div>
                      {email.status !== 'active' ? (
                        <span className="flex items-center gap-1 text-xs font-semibold" style={{color:'#34d399'}}>
                          ✓ {email.status.replace('_',' ')}
                        </span>
                      ) : <Badge value={email.status}/>}
                      {isIT && isActive && email.status === 'active' && (
                        <div className="flex gap-2">
                          {['disabled','password_changed','forwarded'].map(action => (
                            <button key={action} onClick={() => { setActionModal({type:'email',item:email}); setActionForm({action,remarks:''}); setError(''); }}
                              className="text-xs px-2 py-1 rounded-lg capitalize" style={{background:'rgba(255,255,255,0.06)',color:'#94a3b8',border:'1px solid rgba(255,255,255,0.08)'}}>
                              {action.replace('_',' ')}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs px-4 py-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)',color:'#334155'}}>No email records found</div>}
            </div>

            {/* ACCESSORIES */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={14} style={{color:'#fb923c'}}/>
                <span className="text-sm font-semibold" style={{color:'#94a3b8'}}>Accessories Held ({checklist.checklist?.accessories?.length || 0})</span>
                {(checklist.checklist?.accessories?.length || 0) === 0 && <span className="text-xs" style={{color:'#34d399'}}>✓ All cleared</span>}
              </div>
              {checklist.checklist?.accessories?.length > 0 ? (
                <div className="space-y-2">
                  {checklist.checklist.accessories.map(acc => (
                    <div key={acc.id} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                      <div className="flex-1">
                        <div className="text-sm font-medium" style={{color:'#e2e8f0'}}>{acc.name || `${acc.brand||''} ${acc.model||''}`.trim()}</div>
                        <div className="text-xs" style={{color:'#475569'}}>{acc.accessory_number} · {acc.category}</div>
                      </div>
                      {isIT && isActive && (
                        <button onClick={async () => {
                          try { await api.post(`/offboarding/${selected.id}/accessory/${acc.id}`); await loadChecklist(selected.id); load(); }
                          catch (err) { setError(err.response?.data?.error || 'Failed'); }
                        }} className="text-xs px-3 py-1.5 rounded-lg" style={{background:'rgba(251,146,60,0.12)',color:'#fb923c',border:'1px solid rgba(251,146,60,0.3)'}}>
                          Mark Returned
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs px-4 py-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)',color:'#334155'}}>No accessories assigned</div>}
            </div>

            {/* DONGLE / VOICE CONNECTIONS */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Wifi size={14} style={{color:'#22d3ee'}}/>
                <span className="text-sm font-semibold" style={{color:'#94a3b8'}}>
                  Dongles &amp; Voice Numbers ({checklist.checklist?.connections?.length || 0})
                </span>
                {(checklist.checklist?.connections?.length || 0) === 0 && <span className="text-xs" style={{color:'#34d399'}}>✓ All cleared</span>}
              </div>
              {checklist.checklist?.connections?.length > 0 ? (
                <div className="space-y-2">
                  {checklist.checklist.connections.map(cn => (
                    <div key={`${cn.type}-${cn.id}`} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={{background:'rgba(34,211,238,0.05)',border:'1px solid rgba(34,211,238,0.15)'}}>
                      <div className="flex-1">
                        <div className="text-sm font-mono" style={{color:'#67e8f9'}}>{cn.number}</div>
                        <div className="text-xs" style={{color:'#475569'}}>
                          {cn.type === 'dongle' ? 'Data Dongle / Connection' : 'Voice Number (SIM)'}
                          {cn.isp_name ? ` · ${cn.isp_name}` : ''}
                        </div>
                      </div>
                      {isIT && isActive && (
                        <div className="flex gap-2">
                          <button onClick={() => handleConnection(cn, 'return')}
                            title="SIM/dongle handed back — the line stays active and can be reassigned"
                            className="text-xs px-3 py-1.5 rounded-lg"
                            style={{background:'rgba(34,211,238,0.12)',color:'#22d3ee',border:'1px solid rgba(34,211,238,0.3)'}}>
                            Mark Returned
                          </button>
                          <button onClick={() => handleConnection(cn, 'disconnect')}
                            title="Terminate the line with the provider"
                            className="text-xs px-3 py-1.5 rounded-lg"
                            style={{background:'rgba(239,68,68,0.1)',color:'#f87171',border:'1px solid rgba(239,68,68,0.25)'}}>
                            Disconnect
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs px-4 py-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)',color:'#334155'}}>No dongles or voice numbers assigned</div>}
            </div>

            {/* COMPLETE */}
            {isIT && isActive && (
              <div className={`rounded-xl p-4 ${allDone ? 'cursor-pointer' : ''}`}
                style={{background:allDone?'rgba(16,185,129,0.08)':'rgba(255,255,255,0.03)',border:`1px solid ${allDone?'rgba(16,185,129,0.3)':'rgba(255,255,255,0.06)'}`}}>
                <div className="flex items-center gap-3">
                  <CheckCircle size={18} style={{color:allDone?'#34d399':'#334155'}}/>
                  <div className="flex-1">
                    <div className="text-sm font-semibold" style={{color:allDone?'#34d399':'#475569'}}>
                      {allDone ? 'All items cleared — ready to complete' : `Still to hand over: ${stillOpen.join(', ')}`}
                    </div>
                    {!allDone && checklist.checklist?.assets?.length > 0 && (
                      <div className="text-xs mt-0.5" style={{color:'#334155'}}>{checklist.checklist.assets.length} asset(s) still pending</div>
                    )}
                  </div>
                  {allDone && (
                    <button onClick={handleComplete} disabled={saving}
                      className="btn-primary" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
                      {saving ? '...' : 'Complete Offboarding'}
                    </button>
                  )}
                </div>
              </div>
            )}
            {/* CANCEL — employee decided to stay */}
            {isAdmin && isActive && (
              <div className="rounded-xl p-4 flex items-center gap-3 flex-wrap" style={{background:'rgba(239,68,68,0.05)',border:'1px solid rgba(239,68,68,0.2)'}}>
                <Ban size={16} style={{color:'#f87171'}}/>
                <div className="flex-1" style={{minWidth:200}}>
                  <div className="text-sm font-semibold" style={{color:'#f87171'}}>Employee decided to stay?</div>
                  <div className="text-xs mt-0.5" style={{color:'#64748b'}}>Cancel this offboarding — the employee is set back to active and notified, with IT, their HOD and HR copied.</div>
                </div>
                <button onClick={openCancel} className="text-xs px-3 py-1.5 rounded-lg font-medium"
                  style={{background:'rgba(239,68,68,0.15)',color:'#f87171',border:'1px solid rgba(239,68,68,0.35)'}}>
                  Cancel Offboarding
                </button>
              </div>
            )}

            {checklist.status === 'completed' && (
              <div className="rounded-xl p-4" style={{background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.2)'}}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={18} style={{color:'#34d399'}}/>
                    <span className="font-semibold" style={{color:'#34d399'}}>Offboarding Completed</span>
                  </div>
                  <button
                    onClick={() => window.open(`/api/offboarding/${selected.id}/print?token=${localStorage.getItem('token')}`, '_blank')}
                    className="btn-ghost text-xs px-3 py-1.5"
                    style={{color:'#60a5fa',borderColor:'rgba(96,165,250,0.3)'}}
                    title="Print Offboarding Confirmation Sheet">
                    🖨 Print Confirmation
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* CANCEL OFFBOARDING MODAL */}
      {cancelOpen && selected && (
        <Modal title={`Cancel Offboarding — ${selected.employee?.full_name || ''}`} onClose={() => { setCancelOpen(false); setCancelResult(null); }}>
          {!cancelResult ? (
            <div className="space-y-4">
              <ErrMsg msg={cancelError} />
              <div className="px-4 py-3 rounded-xl text-xs space-y-1.5" style={{background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.2)',color:'#cbd5e1'}}>
                <div className="font-semibold" style={{color:'#f87171'}}>What happens when you cancel</div>
                <div>• The offboarding is closed as <strong>Cancelled</strong> and becomes read-only — no more reminders go out.</div>
                <div>• The employee is set back to <strong>active</strong>.</div>
                <div>• An email goes to <strong>{checklist?.employee?.email || selected.employee?.email || 'the employee'}</strong>, CC <strong>IT</strong>, their <strong>HOD</strong>{checklist?.recipients?.hod_email ? ` (${checklist.recipients.hod_email})` : ' (none on file)'} and <strong>HR (synergyhr@clsynergy.com)</strong>, including the reason below.</div>
                <div>• Anything IT already disabled or collected is <strong>not</strong> reversed automatically — it is listed in the email so it can be restored.</div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Reason for cancelling *</label>
                <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3} className="input-dark"
                  placeholder="e.g. Employee withdrew their resignation and will continue in their current role"/>
                <div className="text-xs mt-1" style={{color:'#64748b'}}>This reason is shown to the employee, IT, the HOD and HR.</div>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setCancelOpen(false)} className="btn-ghost flex-1 justify-center">Keep Offboarding</button>
                <button onClick={handleCancel} disabled={cancelling} className="btn-primary flex-1 justify-center"
                  style={{background:'linear-gradient(135deg,#dc2626,#b91c1c)'}}>
                  {cancelling ? 'Cancelling…' : 'Cancel Offboarding'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="px-4 py-3 rounded-xl text-sm" style={{background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.25)',color:'#34d399'}}>
                ✓ {cancelResult.message}
              </div>
              <div className="text-xs space-y-1.5" style={{color:'#94a3b8'}}>
                <div>Employee status: {cancelResult.employee_restored
                  ? <strong style={{color:'#34d399'}}>restored to {cancelResult.employee_status}</strong>
                  : <span style={{color:'#fbbf24'}}>left as “{cancelResult.employee_status || 'unknown'}” — it was changed outside this offboarding, review it in Employees</span>}
                </div>
                <div>Email: {EMAIL_STATUS_TEXT[cancelResult.email?.status] || cancelResult.email?.status}
                  {cancelResult.email?.to && <span style={{color:'#64748b'}}> — To {cancelResult.email.to}{cancelResult.email.cc ? `, CC ${cancelResult.email.cc}` : ''}</span>}
                </div>
              </div>
              {(cancelResult.restore?.access?.length > 0 || cancelResult.restore?.items?.length > 0) && (
                <div className="px-4 py-3 rounded-xl text-xs" style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.25)'}}>
                  <div className="font-semibold mb-1" style={{color:'#fbbf24'}}>IT to restore</div>
                  {[...(cancelResult.restore.access || []), ...(cancelResult.restore.items || [])].map((line, i) => (
                    <div key={i} style={{color:'#cbd5e1'}}>• {line}</div>
                  ))}
                </div>
              )}
              <button onClick={() => { setCancelOpen(false); setCancelResult(null); }} className="btn-primary w-full justify-center">Done</button>
            </div>
          )}
        </Modal>
      )}

      {/* ACTION MODAL */}
      {actionModal && (
        <Modal title={`Handle: ${actionModal.type === 'asset' ? actionModal.item.asset_number : actionModal.type === 'system' ? actionModal.item.system_name : actionModal.item.email_address}`}
          onClose={() => { setActionModal(null); setActionForm({}); }}>
          <div className="space-y-4">
            <ErrMsg msg={error} />

            {actionModal.type === 'asset' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {[['return','Return Asset',<RotateCcw size={14}/>, '#10b981'], ['donate','Donate to Employee',<Gift size={14}/>, '#a855f7']].map(([val, label, icon, color]) => (
                    <button key={val} onClick={() => setActionForm({...actionForm, action: val})}
                      className="flex items-center gap-2 p-4 rounded-xl text-sm font-medium transition-all"
                      style={{background: actionForm.action === val ? `${color}18` : 'rgba(255,255,255,0.04)', color: actionForm.action === val ? color : '#475569', border: `1px solid ${actionForm.action === val ? `${color}40` : 'rgba(255,255,255,0.08)'}` }}>
                      {icon} {label}
                    </button>
                  ))}
                </div>
                {actionForm.action === 'donate' && <div className="px-4 py-3 rounded-xl text-xs" style={{background:'rgba(168,85,247,0.1)',border:'1px solid rgba(168,85,247,0.2)',color:'#c084fc'}}>
                  This asset will be donated to the employee and moved to the Donations register.
                </div>}
                {actionForm.action === 'return' && <div className="px-4 py-3 rounded-xl text-xs" style={{background:'rgba(16,185,129,0.1)',border:'1px solid rgba(16,185,129,0.2)',color:'#34d399'}}>
                  Asset will be marked as <strong>Available</strong> and appear in the pending allocation list.
                </div>}
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Remarks</label>
                  <textarea value={actionForm.remarks || ''} onChange={e => setActionForm({...actionForm, remarks: e.target.value})} rows={3} className="input-dark" placeholder="Condition of asset, notes..." />
                </div>
              </div>
            )}

            {actionModal.type === 'system' && (
              <div className="space-y-3">
                <div className="px-4 py-3 rounded-xl text-sm" style={{background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.2)',color:'#818cf8'}}>
                  Action: <strong className="capitalize">{actionForm.action?.replace('_',' ')}</strong> for {actionModal.item.system_name}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Remarks</label>
                  <textarea value={actionForm.remarks || ''} onChange={e => setActionForm({...actionForm, remarks: e.target.value})} rows={3} className="input-dark" placeholder="Disabling reason, ticket reference..." />
                </div>
              </div>
            )}

            {actionModal.type === 'email' && (
              <div className="space-y-3">
                <div className="px-4 py-3 rounded-xl text-sm" style={{background:'rgba(52,211,153,0.08)',border:'1px solid rgba(52,211,153,0.2)',color:'#34d399'}}>
                  Action: <strong className="capitalize">{actionForm.action?.replace('_',' ')}</strong> for {actionModal.item.email_address}
                </div>
                {actionForm.action === 'forwarded' && (
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Forward To *</label>
                    <input type="email" required value={actionForm.forwarded_to || ''} onChange={e => setActionForm({...actionForm, forwarded_to: e.target.value})} className="input-dark" placeholder="forward@company.com" />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Remarks</label>
                  <textarea value={actionForm.remarks || ''} onChange={e => setActionForm({...actionForm, remarks: e.target.value})} rows={3} className="input-dark" placeholder="Notes..." />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button onClick={() => { setActionModal(null); setActionForm({}); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={handleAction} disabled={saving || (actionModal.type === 'asset' && !actionForm.action)} className="btn-primary flex-1 justify-center">
                {saving ? '...' : 'Confirm'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
